import { AuthClientConfig } from '@auth0/auth0-angular';
import {AuthorizationService, IamConfig, IamService} from '@netfoundry-ui/shared/authorization';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import LogRocket from 'logrocket';
import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { VERSION } from '../environments/version';
import {Router} from "@angular/router";
import {ApiService, BrandingModeService} from "@netfoundry-ui/shared/services";
import {cloneDeep, defer, isEmpty} from "lodash";

declare const gtag;

@Injectable({ providedIn: 'root' })
export class InitializerService {
    static identityConfig: IamConfig = {
        url: environment.identityConfig.url,
        invitationUrl: window.location.origin + '/invitation',
        intermediateReturnUrl: environment.identityConfig.intermediateReturnUrl,
        loginReturnUrl: environment.identityConfig.loginReturnUrl,
    };
    static clientSyncsConfig = {
        url: environment.clientSyncsApiUlr,
    };
    static elasticConfig = { url: environment.apiUrl, usePublicApi: true };
    static authorizationConfig = {
        url: environment.authorizationUrl,
    };
    static notificationConfig = {
        url: environment.notificationUrl,
    };
    static authConfig: any = {
        clientId: environment.authconfig.clientID,
        domain: environment.authconfig.domain,
        errorPath: '/login',
        authorizationParams: {
            audience: environment.authconfig.audience,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            redirect_uri: environment.authconfig.callbackURL,
        },
        useRefreshTokens: true,
        cacheLocation: 'localstorage',
        skipRedirectCallback: window.location.pathname === '/oidc-callback',
        httpInterceptor: {
            allowedList: [
                {
                    uriMatcher: (uri) => {
                        if (uri.indexOf('/tenants/label') >= 0) return false;
                        if (uri.indexOf('/organizations/label') >= 0) return false;
                        if (uri.indexOf('/tenants/authorize') >= 0) return false;
                        if (uri.indexOf('/tenants/forgotten-site') >= 0) return false;
                        if (uri.indexOf('authorize-initiate') >= 0) return false;
                        if (uri.indexOf('/identity/v1/identity-provider-types') >= 0) return false;
                        if (uri.indexOf('/rest/v1/countries') >= 0) return false;
                        if (uri.indexOf('/identity/v1/tenants/label/') >= 0) return false;
                        if (uri.indexOf('/invitation?') >= 0) return false;
                        if (uri.indexOf('/customer/v1/email-verification') >= 0) return false;
                        if (uri.indexOf('/customer/v1/otps') >= 0) return false;
                        if (uri.indexOf('nf-console-branding') >= 0) return false;
                        if (uri.indexOf('/edge/management/v1') >= 0) return false;
                        if (uri.indexOf('edge/client/v1') >= 0) return false;
                        if ((uri.indexOf('stored-secrets') >= 0) && (uri.indexOf('?token=') >= 0)) return false;
                        if (uri.indexOf('/identity/v1/realms/label') >= 0) return false;
                        if (uri.indexOf('/assets/styles/darkmode.css') >= 0) return false;
                        // Branding endpoints — GETs are public (no token added so the pre-login
                        // welcome page can theme itself); mutations are handled by the dedicated
                        // allowedList entries below.
                        if (uri.indexOf('/identity/v1/nfconsole/branding') >= 0) return false;
                        if ((uri.indexOf('/frontdoor/certificate-upload') >= 0)) return false;
                        if ((uri.indexOf('/frontdoor/v2/certificate-upload') >= 0)) return false;
                        return !(uri.indexOf('/identity/v1/invitations/') >= 0 && uri.indexOf('/revoke') < 0);
                    },
                },
                // Branding colors/logos mutations still require a bearer token.
                ...(['POST', 'PUT', 'PATCH', 'DELETE'] as const).map((method) => ({
                    uriMatcher: (uri: string) =>
                        uri.indexOf('/identity/v1/nfconsole/branding/colors') >= 0 ||
                        uri.indexOf('/identity/v1/nfconsole/branding/logos') >= 0,
                    httpMethod: method,
                })),
            ],
        },
    };

    static initialize(config: AuthClientConfig, identityConfig: IamConfig, http: HttpClient, handler: HttpBackend, authService: AuthorizationService, iamService: IamService, brandingService: BrandingModeService, apiService: ApiService) {
        return InitializerService.initAuthConfig(config, http, handler, identityConfig, authService, iamService, brandingService, apiService);
    }

    static initLogRocket(): void {
        if (!(environment as any).disableLogRocket) {
            LogRocket.init(`pnjasd/netfoundry-${environment.domain}`, {
                release: `${VERSION.version} ${VERSION.buildDate} ${VERSION.buildTime}`,
                shouldParseXHRBlob: true,
                browser: {
                    urlSanitizer: (url) => {
                        let sanitizedUrl = url;
                        sanitizedUrl = sanitizedUrl.replace(/access_token=([^&]*)/, 'access_token=**redacted**');
                        sanitizedUrl = sanitizedUrl.replace(/identityId=([^&]*)/, 'identityId=**redacted**');
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        sanitizedUrl = sanitizedUrl.replace(/\/client\/(.*)/i, '/client/**redacted**');
                        return sanitizedUrl;
                    },
                },
                network: {
                    requestSanitizer: (request) => {
                        if (request.headers['Authorization']) {
                            request.headers['Authorization'] = '**redacted**';
                        }
                        return request;
                    },

                    responseSanitizer: (response) => {
                        if (response.body) {
                            if (response.body['nfToken']) {
                                response.body = '**redacted**';
                            }
                        }
                        return response;
                    },
                },
            });
            LogRocket.getSessionURL(function (sessionURL) {
              gtag('event', 'logrocket_session', {
                session_url: sessionURL,
              });
            });
        }
    }

    private static initAuthConfig(
        config: AuthClientConfig,
        http: HttpClient,
        handler: HttpBackend,
        identityConfig: IamConfig,
        authService: AuthorizationService,
        iamService: IamService,
        brandingService: BrandingModeService,
        apiService: ApiService
    ) {
        http = new HttpClient(handler);

        const tenantLabel = localStorage.getItem('tenantLabel');
        if (!tenantLabel) {
            const currentUri = window.location?.pathname + window.location?.search;
            if (currentUri.indexOf('/invitation') >= 0) {
                InitializerService.authConfig.errorPath = currentUri;
            }
            const brandingProm = new Promise((resolve, reject) => {
              apiService.realmBrandingInit.subscribe((brandingInit) => {
                if (brandingInit) {
                  resolve(true);
                }
              })
            });
            return () => {
              const newAuthConfig = cloneDeep(InitializerService.authConfig);
              newAuthConfig.authorizationParams = newAuthConfig.authorizationParams || {};
              newAuthConfig.authorizationParams.redirect_uri = window.location.origin + '/callback';
              config.set(newAuthConfig);
              return this.initRealm(iamService, identityConfig, http, apiService).then(() => {
                return brandingProm;
              });
            }
        } else {
            return () => {
              const newAuthConfig = cloneDeep(InitializerService.authConfig);
              newAuthConfig.authorizationParams = newAuthConfig.authorizationParams || {};
              newAuthConfig.authorizationParams.redirect_uri = window.location.origin + '/callback';
              config.set(newAuthConfig);
              return this.initRealm(iamService, identityConfig, http, apiService).then(() => {
                return this.initAuthInterceptor(config, http, identityConfig, authService, tenantLabel, iamService).then(() => {
                  return () => Promise.resolve();
                });;
              });
            };
        }
    }

    private static initAuthInterceptor(
      config: AuthClientConfig,
      http: HttpClient,
      identityConfig: IamConfig,
      authService: AuthorizationService,
      tenantLabel: string,
      iamService: IamService
    ): Promise<any> {
      let redirectUri = environment.authconfig.callbackURL;
      const isVanityUrl = localStorage.getItem('isVanityUrl') === 'true';
      if (isVanityUrl) {
        redirectUri = InitializerService.initVanity(redirectUri, tenantLabel);
      }
      let headers = new HttpHeaders();
      headers = headers.set('Content-Type', 'application/json; charset=utf-8');
      const fullpath = `${identityConfig.url}tenants/authorize-initiate`;
      const intermediateReturnUrl = window.location.origin + '/login';
      const body = { tenantLabel: tenantLabel, intermediateReturnUrl: intermediateReturnUrl };
      const authInitProm = lastValueFrom(http.post(fullpath, JSON.stringify(body), { headers: headers })).then(
        (result: any) => {
          InitializerService.authConfig.authorizationParams = InitializerService.authConfig.authorizationParams || {};
          InitializerService.authConfig.authorizationParams.nfToken = result.nfToken;
          InitializerService.authConfig.authorizationParams.redirect_uri = redirectUri;
          return config.set(InitializerService.authConfig);
        }
      ).catch((error) => {
        return config.set(InitializerService.authConfig);
      });
      const authSelfProm = new Promise((resolve, reject) => {
        authInitProm.then((authInitResult) => {
          authService.getAuthorization().then((authSelfResult) => {
            resolve(authSelfResult);
          }).catch((error) => {
            resolve({});
          })
        }).catch((err) => {
          resolve(err);
        });
      });
      return authSelfProm;
    }

    private static initRealm(iamService, identityConfig, http, apiService: ApiService): Promise<any> {
      const hostname = window.location.hostname;
      const isRootDomain = hostname === environment.originDomain;
      let label;
      if (isRootDomain) {
        label = 'netfoundry';
      } else {
        const subdomainStr = hostname.replace(environment.originDomain, '');
        let subdomains = subdomainStr.split('.');
        subdomains = subdomains.filter((subdomain) => {
          return !isEmpty(subdomain);
        });
        if (subdomains.length === 1) {
          label = subdomains[0];
        } else if (subdomains.length === 2) {
          label = subdomains[1];
        } else {
          window.location.href = environment.identityConfig.loginReturnUrl;
          console.error('Unexpected url pattern. Redirecting to root origin login page.');
          return;
        }
      }
      let headers = new HttpHeaders();
      headers = headers.set('Content-Type', 'application/json; charset=utf-8');
      const realmPath = `${identityConfig.url}realms/label/${label}`;
      return lastValueFrom(http.get(realmPath, { headers: headers })).then((result: any) => {
        localStorage.setItem('currentRealmLabel', result?.name);
        localStorage.setItem('currentRealmId', result?.id);
        apiService.setCurrentRealm(result);
        return result;
      }).catch(() => {
        const realmPath = `${identityConfig.url}realms/label/netfoundry`;
        return lastValueFrom(http.get(realmPath, { headers: headers })).then((result: any) => {
          console.log(result);
          localStorage.setItem('currentRealmLabel', result?.name);
          localStorage.setItem('currentRealmId', result?.id);
          apiService.setCurrentRealm(result);
          return result;
        })
      });
    }

    private static initBranding(brandingService: any, apiService: any): Promise<any> {
      const currentRealm = apiService.currentRealm?.value;
      return brandingService.loadBrandingStylesheets(currentRealm?.id, undefined);
    }

    private static initVanity(redirectUri: string, tenantLabel: string) {
        let newUri = '';
        if (redirectUri.indexOf('https://') >= 0) {
            const redirectLocation = redirectUri.slice(8);
            newUri = `https://${tenantLabel}.${redirectLocation}`;
        } else {
            const redirectLocation = redirectUri.slice(7);
            newUri = `http://${tenantLabel}.${redirectLocation}`;
        }
        return newUri;
    }
}
